/* global insightsViewsUrl insightsViewDeleteUrl */

import { useCallback, useEffect, useState } from 'react';

import useApiRequest from 'Hooks/useApiRequest';

/**
 * Manages the user's saved Insights views: seeds the list from metadata, then
 * adds / removes views in place so the panel updates without a page reload.
 *
 * Persistence is server-authoritative — the API owns id generation and the
 * canonical list; this hook only mirrors it in local state.
 *
 * @ticket IB-644
 */
export default function useInsightsView(initialViews) {
    const [views, setViews] = useState(initialViews || []);

    // Re-seed once metadata arrives (initialViews is null on first render).
    useEffect(() => {
        if (Array.isArray(initialViews)) {
            setViews(initialViews);
        }
    }, [initialViews]);

    const saveApi = useApiRequest({
        url: typeof insightsViewsUrl !== 'undefined' ? insightsViewsUrl : '',
        successMessage: $t('View saved'),
    });
    const deleteApi = useApiRequest({ successMessage: $t('View deleted') });

    const saveView = useCallback(
        (name, view, chartType) =>
            saveApi.post({ name, view, chartType }).then(({ data }) => {
                setViews((current) => [...current, data]);
                return data;
            }),
        [saveApi],
    );

    const deleteView = useCallback(
        (id) => {
            // Build the per-id URL and override it on the request (passing it
            // through the hook's `replace` would mutate its cached url across calls).
            const base = typeof insightsViewDeleteUrl !== 'undefined' ? insightsViewDeleteUrl : '';
            const url = base.replace('__ID__', encodeURIComponent(id));

            return deleteApi.request({ method: 'delete', url }).then(() => {
                setViews((current) => current.filter((v) => v.id !== id));
            });
        },
        [deleteApi],
    );

    return { views, saveView, deleteView };
}
